/**
 * Slice 37 — Cleaner nag for VA "Need photo" (never blocks Next / Finish).
 * Canvas C1 / L5 copy.
 */
import { useMemo, useRef, useState } from "react";
import { Camera, Loader2, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { useUnitItems } from "@/hooks/useUnitItems";
import { usePropertyRooms } from "@/hooks/usePropertyRooms";
import { normalizeRoomName } from "@/lib/roomUtils";
import { useUnitPhotoAsks, useUpdateUnitPhotoAsk } from "@/hooks/useUnitPhotoAsks";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import {
  appendPhotoCaptureRecord,
  uploadInAppCleaningPhoto,
} from "@/lib/cleaningPhotoCapture";
import {
  collectSimplePhotoUrls,
  readSimpleFlowState,
  type SimpleFlowState,
} from "@/lib/simpleTurnover";
import { toast } from "@/hooks/use-toast";
import { useTranslation } from "@/hooks/useTranslation";

interface PhotoNeedNagProps {
  propertyId: string;
  taskId: string;
  /** Drop-keys context uses the “Still inside?” line from the canvas. */
  variant?: "default" | "drop_keys";
  className?: string;
}

export function PhotoNeedNag({
  propertyId,
  taskId,
  variant = "default",
  className,
}: PhotoNeedNagProps) {
  const { t, tDynamic } = useTranslation();
  const { data: asks = [] } = useUnitPhotoAsks(taskId, { refetchInterval: 5000 });
  const { data: items = [] } = useUnitItems(propertyId);
  const { data: propertyRooms = [] } = usePropertyRooms(propertyId);
  const updateAsk = useUpdateUnitPhotoAsk();
  const [busy, setBusy] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const openAsk = useMemo(() => {
    const open = asks.find((a) => a.status === "open");
    if (!open) return null;
    const item = items.find((i) => i.id === open.unit_item_id);
    return { ask: open, item };
  }, [asks, items]);

  const { data: requesterName } = useQuery({
    queryKey: ["photo-ask-requester", openAsk?.ask.requested_by],
    queryFn: async () => {
      if (!openAsk?.ask.requested_by) return null;
      const { data } = await supabase
        .from("profiles")
        .select("full_name")
        .eq("id", openAsk.ask.requested_by)
        .maybeSingle();
      return data?.full_name?.trim() || null;
    },
    enabled: !!openAsk?.ask.requested_by,
  });

  if (!openAsk?.item) return null;

  const itemName = openAsk.item.name;
  const room = openAsk.item.room;

  const dismiss = async () => {
    setBusy(true);
    try {
      await updateAsk.mutateAsync({
        id: openAsk.ask.id,
        cleaningTaskId: taskId,
        status: "dismissed",
      });
    } catch (err) {
      toast({
        title: t("couldntDismiss"),
        description: err instanceof Error ? err.message : undefined,
        variant: "destructive",
      });
    } finally {
      setBusy(false);
    }
  };

  const onPickFile = async (file: File | null) => {
    if (!file || !openAsk) return;
    setBusy(true);
    try {
      const { url, record } = await uploadInAppCleaningPhoto({
        propertyId,
        taskId,
        moment: "walk",
        blob: file,
        storagePath: `${propertyId}/${taskId}/simple-before/walk/${crypto.randomUUID()}.jpg`,
      });
      const { data: task } = await supabase
        .from("cleaning_tasks")
        .select("entry_walkthrough_state")
        .eq("id", taskId)
        .maybeSingle();
      const simple = readSimpleFlowState(task?.entry_walkthrough_state);
      const before = collectSimplePhotoUrls(simple, "before");
      // Slice 54: the photo belongs to the room the asked-for item is in.
      const roomId = propertyRooms.find(
        (r) => !r.archived_at && normalizeRoomName(r.name) === normalizeRoomName(openAsk.item?.room || ""),
      )?.id;
      const walkByRoom = simple.walk_by_room
        ? roomId
          ? {
              ...simple.walk_by_room,
              [roomId]: {
                ...simple.walk_by_room[roomId],
                photo_urls: [...(simple.walk_by_room[roomId]?.photo_urls ?? []), url],
              },
            }
          : simple.walk_by_room
        : undefined;
      const nextState: SimpleFlowState = {
        ...simple,
        simple_flow: true,
        before_photo_urls: [...before, url],
        ...(walkByRoom ? { walk_by_room: walkByRoom } : {}),
        photo_captures: appendPhotoCaptureRecord(simple.photo_captures, record),
      };
      const { error } = await supabase
        .from("cleaning_tasks")
        .update({ entry_walkthrough_state: nextState })
        .eq("id", taskId);
      if (error) throw error;
      await updateAsk.mutateAsync({
        id: openAsk.ask.id,
        cleaningTaskId: taskId,
        status: "fulfilled",
        fulfilledPhotoUrl: url,
      });
      toast({ title: t("photoSent") });
    } catch (err) {
      toast({
        title: t("couldntUpload"),
        description: err instanceof Error ? err.message : undefined,
        variant: "destructive",
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className={cn(
        "flex flex-col gap-2.5 rounded-[16px] border-[1.5px] border-[#f0d3d0] bg-[#fff8f7] px-3.5 py-3",
        className,
      )}
      data-tour="tour-photo-need-nag"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-[15px] font-bold text-[#0f1720]">
            {t("photoNeeded").replace("{{label}}", tDynamic(itemName))}
          </div>
          <div className="text-[12.5px] font-medium text-[#8494a2]">
            {room ? `${room} · ` : ""}
            {requesterName ? t("askedByVa").replace("VA", requesterName) : t("askedByVa")}
          </div>
          {variant === "drop_keys" ? (
            <div className="mt-1 text-[13px] font-medium text-[#b45309]">
              {t("stillInsideGrabIt")}
            </div>
          ) : null}
        </div>
        <button
          type="button"
          aria-label={t("dismiss")}
          disabled={busy}
          onClick={() => void dismiss()}
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[#8494a2] hover:bg-white"
        >
          <X className="h-4 w-4" strokeWidth={2.6} />
        </button>
      </div>
      <div className="flex gap-2">
        <button
          type="button"
          disabled={busy}
          onClick={() => fileRef.current?.click()}
          className="flex h-11 flex-1 items-center justify-center gap-2 rounded-xl bg-[#24a89a] text-[15px] font-bold text-white disabled:opacity-50"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Camera className="h-4 w-4" />}
          {t("takePhoto")}
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={() => void dismiss()}
          className="flex h-11 items-center justify-center rounded-xl border-[1.5px] border-[#e0c3c0] bg-white px-4 text-[15px] font-bold text-[#b4453c]"
        >
          {t("dismiss")}
        </button>
      </div>
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0] ?? null;
          e.target.value = "";
          void onPickFile(f);
        }}
      />
    </div>
  );
}
